import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Target, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import useDocumentTitle from "../hooks/useDocumentTitle";

export default function ForgotPassword() {
  useDocumentTitle("Forgot password");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter your email"); return; }
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send reset link");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 grid place-items-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <span className="h-8 w-8 rounded-md bg-[#002FA7] text-white grid place-items-center">
            <Target className="h-4 w-4" strokeWidth={2.5} />
          </span>
          <span className="font-display font-black tracking-tighter text-zinc-950">
            Qa<span className="text-[#002FA7]">Testology</span>
          </span>
        </Link>

        <Card className="p-6 sm:p-8">
          {!sent ? (
            <>
              <div className="mb-6">
                <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1">reset</div>
                <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter text-zinc-950">
                  Forgot your password?
                </h1>
                <p className="text-sm text-zinc-600 mt-2">
                  Enter the email on your QaTestology account and we&apos;ll send you a link to set a new one.
                </p>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-1.5"
                    data-testid="forgot-email-input"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white h-11"
                  data-testid="forgot-submit-btn"
                >
                  {sending ? "Sending link…" : "Send reset link"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="forgot-sent-state">
              <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 grid place-items-center text-emerald-700 mb-3">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h1 className="font-display text-2xl font-black tracking-tight text-zinc-950">Check your inbox</h1>
              <p className="text-sm text-zinc-600 mt-2 mb-6">
                If an account exists for <span className="font-mono font-semibold text-zinc-900">{email}</span>,
                a reset link is on its way. The link expires in 30 minutes.
              </p>
              <div className="text-xs text-zinc-500 tag-mono flex items-center justify-center gap-1 mb-6">
                <Mail className="h-3 w-3" /> also check your spam folder
              </div>
              <Button
                onClick={() => { setSent(false); setEmail(""); }}
                variant="outline"
                data-testid="forgot-resend-btn"
                className="w-full"
              >
                Send to a different email
              </Button>
            </div>
          )}

          <div className="mt-6 pt-5 border-t border-zinc-100 text-sm text-center">
            <Link to="/login" className="inline-flex items-center gap-1 text-zinc-600 hover:text-[#002FA7]" data-testid="back-to-login-link">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
